import { inject, Injectable } from '@angular/core';
import { ConfigRepository } from './config.repository';
import { BasketRepository } from './basket.repository';
import { MenuListRepository } from './menu-list.repository';
import { catchError, combineLatest, combineLatestWith, distinctUntilChanged, filter, finalize, first, map, Observable, of, pairwise, shareReplay, startWith, Subject, Subscription, switchMap, take, tap } from 'rxjs';
import {
  CoordinatesDto,
  CostRule,
  CustomerDto,
  CustomerProfileDto,
  DeliveryType,
  FreeItem,
  MenuItemSizeDto,
  OrderItemDto,
  OutletDto,
  OutletMode,
  PastOrderDto,
  PastOrderItemDto,
  PaymentType,
  ProcessBasketRequestDto,
  ProcessBasketResponse,
  RelatedProduct,
  StoredOrderItem
} from '../../api/v1/models';
import { BasketItem } from '../interfaces/basket-item.interface';
import { StatusMessageService } from '../services/status-message.service';
import { createState, deepFreeze, elfHooks, select, setProp, Store, withProps } from '@ngneat/elf';
import { selectIsRequestPending, selectRequestStatus, updateRequestStatus, withRequestsStatus } from '@ngneat/elf-requests';
import { UIMenuItemDto } from '../interfaces/ui-menu-item-dto.interface';
import { Router } from '@angular/router';
import { Modifier } from '../interfaces/modifier.interface';
import { excludeKeys, localStorageStrategy, persistState } from '@ngneat/elf-persist-state';
import { AddressDto } from '../../api/v1/models/address-dto';
import { MarketingCampaign } from '../interfaces/marketing-campaign.interface';
import { DeliveryAddress } from '../interfaces/delivery-address.interface';
import { getTimeZoneOffset } from '../utils/get-time-intervals';
import { BasketService } from '../../api/v1/services/basket.service';
import { environment } from '../../../environments/environment';
import { PriceCalculationService } from '../services/price-calculation.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

interface RemovableItems {
  key: string,
  name: string,
}

const requiredDeliveryTypesToOutletModes: { [key: string]: DeliveryType[] } = {
  'PICKUP': [DeliveryType.Pickup],
  'DELIVERY': [DeliveryType.Delivery],
  'PICKUP_DELIVERY': [DeliveryType.Pickup, DeliveryType.Delivery],
  'EATIN': [DeliveryType.Eatin],
};

interface SessionStateModel {
  selectedDeliveryType: DeliveryType | null,
  preprocessedBasketId: string | null,
  balanceToApply: Map<string, number>,
  basket: ProcessBasketRequestDto | null,
  isAuth: boolean,
  customer: CustomerProfileDto | null,
  isBonusPayment: boolean,
  bonusPaymentSum: number | null,
  isMustProcess: boolean,
  maxBonusPayment: number | null,
  exchangeRate: number | null,
  onlinePaymentOrderId: string | null,
  tablePosId: string | null,
  tableId: string | null,
  isTableFromLink: boolean,
  numberOfGuests: number,
  dtmTime: string | null,
  deliveryAddress: DeliveryAddress | null,
  deliveryAddressCoordinate: CoordinatesDto | null,
  freeItems: FreeItem[] | null,
  freeItemsMap: Map<string, string[]> | null,
  activeLabelFilter: string[] | null,
  activeAllergenFilter: string[] | null,
  showFilterBar: boolean,
  processTotalPrice: number | null,
  processDeliveryPrice: number | null,
  processFullCost: number | null,
}

const {state: sessionState, config: sessionStateConfig} = createState(
  withProps<SessionStateModel>({
    selectedDeliveryType: null,
    preprocessedBasketId: null,
    balanceToApply: new Map<string, number>(),
    basket: null,
    isAuth: false,
    customer: null,
    isBonusPayment: false,
    bonusPaymentSum: null,
    isMustProcess: true,
    maxBonusPayment: null,
    exchangeRate: null,
    onlinePaymentOrderId: null,
    tablePosId: null,
    tableId: null,
    isTableFromLink: false,
    numberOfGuests: 1,
    dtmTime: null,
    deliveryAddress: null,
    deliveryAddressCoordinate: null,
    freeItems: null,
    freeItemsMap: null,
    activeLabelFilter: null,
    activeAllergenFilter: null,
    showFilterBar: false,
    processTotalPrice: null,
    processDeliveryPrice: null,
    processFullCost: null,
  }),
  withRequestsStatus<'basketPreprocessing'>(),
  withRequestsStatus<'couponStatusFetching'>(),
);
const sessionStore = new Store({state: sessionState, name: 'SessionStore', config: sessionStateConfig});

persistState(sessionStore, {
  key: 'sessionState',
  storage: localStorageStrategy,
  source: () => sessionStore.pipe(excludeKeys(['basket', 'requestsStatus', 'freeItems', 'activeLabelFilter', 'activeAllergenFilter', 'showFilterBar', 'freeItemsMap'])),
});

if(!environment.production) {
  elfHooks.registerPreStateInit(initialState => deepFreeze(initialState));
}

@UntilDestroy()
@Injectable({providedIn: 'root'})
export class SessionRepository {
  private configRepository = inject(ConfigRepository);
  private basketRepository = inject(BasketRepository);
  private menuRepository = inject(MenuListRepository);
  private statusMessage = inject(StatusMessageService);
  private router = inject(Router);
  private basketServiceApi = inject(BasketService);
  private priceCalculationService = inject(PriceCalculationService);

  constructor() {
    /**
     * Определяем начальный тип доставки на основании OutletMode;
     * Проверяем вхождение текущего DeliveryType в возможность работы с текущим OutletMode;
     */
    combineLatest([
      this.configRepository.outletMode$,
      this.selectedDeliveryType$,
    ]).pipe(
      first(),
      map(([outletMode, selectedDeliveryType]) => {
        if(!outletMode) {
          return null;
        }

        const currentConfig = requiredDeliveryTypesToOutletModes[outletMode];
        if(currentConfig && selectedDeliveryType && currentConfig.indexOf(selectedDeliveryType) < 0) {
          selectedDeliveryType = null;
        }

        if(outletMode === OutletMode.Qr) {
          return null;
        }

        if(outletMode === OutletMode.Eatin) {
          return DeliveryType.Eatin;
        }

        if(selectedDeliveryType) {
          return selectedDeliveryType;
        }

        if(outletMode === OutletMode.Delivery || outletMode === OutletMode.PickupDelivery) {
          return DeliveryType.Delivery;
        }

        return DeliveryType.Pickup;
      })
    ).subscribe(initialType => this.setDeliveryType(initialType));

    this.checkBasketAvailable$.subscribe();

    // Обновляем цены в корзине при смене активного outlet
    this.configRepository.activeOutletId$.pipe(
      distinctUntilChanged(),
      filter((outletId): outletId is number => !!outletId),
      untilDestroyed(this)
    ).subscribe(outletId => {
      this.basketRepository.updatePricesForOutlet(outletId);
    });
  }

  isBasketProcessing$ = sessionStore.pipe(selectRequestStatus('basketPreprocessing'));
  isCouponStatusFetching$ = sessionStore.pipe(selectIsRequestPending('couponStatusFetching'));
  preprocessedBasketId$: Observable<string | null> = sessionStore.pipe(select(state => state.preprocessedBasketId));
  isMustProcess$: Observable<boolean> = sessionStore.pipe(select(state => state.isMustProcess));
  isBonusPayment$: Observable<boolean> = sessionStore.pipe(select(state => state.isBonusPayment));
  maxBonusPayment$: Observable<number | null> = sessionStore.pipe(select(state => state.maxBonusPayment));
  exchangeRate$: Observable<number | null> = sessionStore.pipe(select(state => state.exchangeRate));
  basket$: Observable<ProcessBasketRequestDto | null> = sessionStore.pipe(map(source => source.basket));
  currentTablePosId$: Observable<string | null> = sessionStore.pipe(select(state => state.tablePosId));
  isTableFromLink$: Observable<boolean> = sessionStore.pipe(select(state => state.isTableFromLink));
  DTMTime$: Observable<string | null> = sessionStore.pipe(select(state => state.dtmTime));
  activeLabelFilter$ = sessionStore.pipe(select(state => state.activeLabelFilter));
  activeAllergenFilter$ = sessionStore.pipe(select(state => state.activeAllergenFilter));
  showFilterBar$ = sessionStore.pipe(select(state => state.showFilterBar));

  activeFilterCount$: Observable<number> = combineLatest([
    this.activeLabelFilter$,
    this.activeAllergenFilter$
  ]).pipe(
    map(([labelFilters, allergenFilters]) => (labelFilters?.length ?? 0) + (allergenFilters?.length ?? 0))
  );

  setDTMTime(time: string | null): void {
    sessionStore.update(setProp('dtmTime', time));
  }

  setSelectedTablePosId(tablePosId: string | null, isTableFromLink: boolean = false): void {
    sessionStore.update(state => ({...state, tablePosId, isTableFromLink}));
  }

  clearSelectedTable(): void {
    sessionStore.update(state => ({...state, tablePosId: null, isTableFromLink: false}));
  }

  setSelectedTableId(tableId: string | null): void {
    sessionStore.update(state => ({...state, tableId}));
  }

  updateExchangeRate(exchangeRate: number): void {
    sessionStore.update(state => ({...state, exchangeRate}));
  }

  setIsProcess(isMustProcess: boolean): void {
    sessionStore.update(state => ({...state, isMustProcess}));
  }

  setNumberOfGuest(numberOfGuests: number): void {
    sessionStore.update(state => ({...state, numberOfGuests}));
  }

  setBonusPayment(value: boolean): void {
    sessionStore.update(state => ({
      ...state,
      isBonusPayment: value,
      bonusPaymentSum: value ? state.maxBonusPayment : 0,
      //TODO: заменить на флаг, после ec-1000
      basket: {...state.basket, bonusPaymentSum: value ? state.maxBonusPayment : 0} as ProcessBasketRequestDto
    }));
  }

  unsetDeliveryDateTime(): void {
    sessionStore.update(state => ({
      ...state,
      basket: {...state.basket, deliveryDateTime: null} as ProcessBasketRequestDto
    }));
  }

  get guestNumberCount(): number {
    return sessionStore.query(state => state.numberOfGuests);
  }

  get onlinePaymentOrderId(): string | null {
    return sessionStore.query(state => state.onlinePaymentOrderId);
  }

  setOnlinePaymentOrderId(id: string | null): void {
    sessionStore.update(setProp('onlinePaymentOrderId', id));
  }

  get preprocessedBasketId(): string | null {
    return sessionStore.query(state => state.preprocessedBasketId);
  }

  setPreprocessBasketId(basketId: string | null): void {
    sessionStore.update(setProp('preprocessedBasketId', basketId));
  }

  get tableId(): string | null {
    return sessionStore.query(state => state.tableId);
  }

  private basketChanged$ = this.basket$.pipe(
    pairwise(),
    map(([previous, current]) => previous != current),
    distinctUntilChanged()
  );

  private itemsChanged$ = this.basketRepository.items$.pipe(
    map(items => items.map(i => `i.itemKey|${i.count}`).join('|')),
    pairwise(),
    map(([previous, current]) => previous != current),
    distinctUntilChanged()
  );

  processBasketOnChange(): Observable<ProcessBasketResponse> {
    return combineLatest([
      this.basketChanged$.pipe(startWith(false)),
      this.itemsChanged$.pipe(startWith(false)),
    ]).pipe(
      filter(([basketChanged, itemsChanged]) => basketChanged || itemsChanged),
      switchMap(() => this.processBasket())
    );
  }

  private processPendingTimeout: ReturnType<typeof setTimeout>;

  processBasket(): Observable<any> {
    return combineLatest([
      this.basket$,
      this.basketRepository.items$,
      this.preprocessedBasketId$,
      this.configRepository.paymentsTypes$,
    ]).pipe(
      first(),
      filter(([basket, basketItems, basketId, paymentsTypes]) => {
        return !!paymentsTypes && !!basket && basketItems && !!basketItems.length;
      }),
      filter(([basket]) => this.isPreprocessBasketValid(basket!)),
      tap(() => {
        /*
        * updateRequestStatus странно себя ведет. Срабатывает дважды и во второй раз проставляет значение,
        * которое должно подставляться в subscribe.
        * Есть подозрение, что он каким-то способом смотрит поток, в котором находится
        * и на его основе сам ставит значения.
        * */
        this.processPendingTimeout = setTimeout(() =>
            sessionStore.update(updateRequestStatus('basketPreprocessing', 'pending')),
          25
        );
      }),

      switchMap(([basket, basketItems, preprocessedBasketId]) => {
        return this.basketServiceApi.apiV1BasketProcessPost$Json({
          body: {
            ...basket!,
            id: preprocessedBasketId,
            items: this.prepareProcessItems(basketItems)
          }
        });
      }),

      map(data => {
        if(data.error || !data.result) {
          throw data;
        }
        return data.result;
      }),

      tap(data => this.updateAll(data)),

      catchError(error => {
        sessionStore.update(updateRequestStatus('basketPreprocessing', 'error', error));
        throw error;
      }),

      finalize(() => {
        clearTimeout(this.processPendingTimeout);
        sessionStore.update(updateRequestStatus('basketPreprocessing', 'success'));
      }),
    );
  }

  private isPreprocessBasketValid(basket: ProcessBasketRequestDto): boolean {
    if(basket.selectedDeliveryType === DeliveryType.Delivery) {
      if(!basket.customer) {
        return false;
      }
      if(!basket.deliveryZoneId) {
        return false;
      }
      if(!basket.deliveryDateTime) {
        return false;
      }
    }

    if(basket.selectedDeliveryType === DeliveryType.Pickup) {
      if(!basket.customer) {
        return false;
      }
      if(!basket.deliveryDateTime) {
        return false;
      }
    }

    return true;
  }

  private prepareProcessItems(basketItems: BasketItem[]): OrderItemDto[] {
    const items: OrderItemDto[] = [];
    basketItems.forEach(basketItem => {
      const orderItem: OrderItemDto = {} as OrderItemDto;
      orderItem.sku = basketItem.size ? basketItem.size.sku : basketItem.item.sku;
      orderItem.quantity = basketItem.count;
      orderItem.modifiers = basketItem.modifiers;
      orderItem.itemKey = basketItem.itemKey;

      orderItem.categoryId = basketItem.item.categoryId;
      orderItem.itemId = basketItem.item.itemId;

      items.push(orderItem);
    });

    return items;
  }

  basketRelatedItems$: Observable<UIMenuItemDto[]> = this.basketRepository.items$.pipe(
    switchMap(basketItems => {
      let relatedProducts: RelatedProduct[] = [];

      basketItems.forEach(basketItem => {
        relatedProducts = relatedProducts.concat(basketItem.size.relatedProducts);
      });

      return this.menuRepository.getRelatedProducts(
        relatedProducts.filter(p => !this.basketRepository.isItemInBasket(p.menuCategoryId + p.menuItemId))
      );
    })
  );

  selectedDeliveryType$: Observable<DeliveryType | null> = sessionStore.pipe(
    select((state) => state.selectedDeliveryType)
  );

  get selectedDeliveryType(): DeliveryType | null {
    return sessionStore.query(state => state.selectedDeliveryType);
  }

  isDelivery$: Observable<boolean> = sessionStore.pipe(
    select(state => state.selectedDeliveryType === DeliveryType.Delivery),
    shareReplay({refCount: false, bufferSize: 1}),
  );

  get isDelivery(): boolean {
    return sessionStore.query((state) => state.selectedDeliveryType === DeliveryType.Delivery);
  }

  totalDiscount$: Observable<number> = this.basketRepository.items$.pipe(
    map((basketItems) => basketItems.reduce((sum, item) => sum + (item.appliedDiscount || 0), 0))
  );

  totalCount$: Observable<number> = this.basketRepository.items$.pipe(
    map(basketItems => basketItems.reduce((sum, current) => sum + current.count, 0))
  );

  processTotalPrice$: Observable<number | null> = sessionStore.pipe(select(state => state.processTotalPrice));
  processDeliveryPrice$: Observable<number | null> = sessionStore.pipe(select(state => state.processDeliveryPrice));
  processFullCost$: Observable<number | null> = sessionStore.pipe(select(state => state.processFullCost));

  totalPrice$: Observable<number> = combineLatest([
    this.basketRepository.items$,
    this.configRepository.activeOutletId$,
  ]).pipe(
    map(([basketItems, activeOutletId]) => {
      if(!activeOutletId) {
        return 0;
      }
      return basketItems.reduce((sum, item) => {
        return sum + item.totalPrice - (item.appliedDiscount || 0);
      }, 0);
    }),
    shareReplay({refCount: true, bufferSize: 1}),
  );

  actualCostRule$: Observable<CostRule | null> = combineLatest([
    this.totalPrice$,
    this.configRepository.activeCostRules$,
  ]).pipe(
    map(([totalPrice, costRules]) => {
      if(!costRules || !costRules.length) {
        return null;
      }

      let maxSum: number = 0;

      for(let i = 0; i < costRules.length; i++) {
        maxSum = costRules[i].maxSum as number;
        if(!costRules[i].maxSum) {
          maxSum = Infinity;
        }
        if(totalPrice < (maxSum as number)) {
          return costRules[i];
        }
      }

      return null;
    }),
    shareReplay({refCount: true, bufferSize: 1}),
  );

  nextActualCostRule$: Observable<CostRule | null> = this.actualCostRule$.pipe(
    combineLatestWith(this.configRepository.activeCostRules$),
    map(([actualCostRule, activeCostRules]) => {
      if(!actualCostRule || !activeCostRules || !activeCostRules.length) {
        return null;
      }

      for(let i = 0; i < activeCostRules.length; i++) {
        if(actualCostRule === activeCostRules[i]) {
          return activeCostRules[i + 1] || null;
        }
      }

      return null;
    })
  );

  minimalOrderPrice$: Observable<number> = this.configRepository.activeCostRules$.pipe(
    map(rules => {
      if(!rules || !rules.length) {
        return 0;
      }
      return rules[0].minSum || 0;
    })
  );

  deliveryPrice$: Observable<number> = this.actualCostRule$.pipe(map(rule => rule?.cost || 0));

  isFreeDelivery$: Observable<boolean> = this.actualCostRule$.pipe(map((cost) => !cost));

  actualDeliveryPrice$: Observable<number> = combineLatest([
    this.deliveryPrice$,
    this.isDelivery$,
    this.isFreeDelivery$
  ]).pipe(
    map(([deliveryPrice, isDelivery, isFreeDelivery]) => isFreeDelivery || !isDelivery ? 0 : deliveryPrice)
  );

  nextDeliveryLimit$: Observable<number | null> = this.actualCostRule$.pipe(
    combineLatestWith(this.totalPrice$),
    map(([costRule, totalPrice]) => costRule?.maxSum ? costRule.maxSum - totalPrice : null)
  );

  needToAdd$: Observable<number> = combineLatest([
    this.totalPrice$,
    this.minimalOrderPrice$
  ]).pipe(
    map(([totalPrice, minimalOrderPrice]) => minimalOrderPrice - totalPrice)
  );

  sumToPay$: Observable<number> = combineLatest([
    this.totalPrice$,
    this.actualDeliveryPrice$,
    this.isFreeDelivery$,
    this.isDelivery$,
    this.totalDiscount$,
    this.processTotalPrice$,
    this.processDeliveryPrice$,
  ]).pipe(
    map(([totalPrice, actualDeliveryPrice, isFreeDelivery, isDelivery, totalDiscount, processTotalPrice, processDeliveryPrice]) => {
      const deliveryPrice = isDelivery && !isFreeDelivery ? actualDeliveryPrice : 0;
      const sumToPay = totalPrice + deliveryPrice - (totalDiscount ? totalDiscount : 0);

      if(processTotalPrice && processDeliveryPrice === null) {
        processTotalPrice += deliveryPrice;
      }

      return processTotalPrice ?? sumToPay;
    })
  );

  maxBonusPaymentSum$: Observable<number | null> = combineLatest([
    this.sumToPay$,
    this.maxBonusPayment$,
    this.exchangeRate$
  ]).pipe(map(([sumToPay, maxBonusPayment, exchangeRate]) => {
    if(exchangeRate && exchangeRate >= 1) {
      return Math.min(sumToPay * exchangeRate, maxBonusPayment ?? 0);
    }
    return Math.min(sumToPay, maxBonusPayment ?? 0);
  }));

  maxBonusPaymentCurrencySum$: Observable<number | null> = combineLatest([
    this.sumToPay$,
    this.maxBonusPayment$,
    this.exchangeRate$
  ]).pipe(
    map(([sumToPay, maxBonusPayment, exchangeRate]) => exchangeRate && exchangeRate >= 1
      ? Math.min(sumToPay, (maxBonusPayment ?? 0) / exchangeRate)
      : Math.min(sumToPay, maxBonusPayment ?? 0)
    ));

  isMinimalOrderPrice$: Observable<boolean> = combineLatest([
    this.minimalOrderPrice$,
    this.totalPrice$,
  ]).pipe(map(([minDeliveryOrderPrice, totalPrice]) => minDeliveryOrderPrice <= totalPrice));

  applicableOutlets$: Observable<OutletDto[]> = combineLatest([
    this.configRepository.outlets$,
    this.selectedDeliveryType$,
  ]).pipe(map(([outlets, selectedDeliveryType]) => {
    switch(selectedDeliveryType) {
      case DeliveryType.Delivery:
        return outlets.filter(outlet =>
          outlet.mode == OutletMode.Delivery || outlet.mode == OutletMode.PickupDelivery);
      case DeliveryType.Pickup:
        return outlets.filter(outlet =>
          outlet.mode == OutletMode.Pickup || outlet.mode == OutletMode.PickupDelivery);
      default:
        return [] as OutletDto[];
    }
  }));

  get hasUnappliedBalance(): number {
    return sessionStore.query((state) => state.balanceToApply.size);
  }

  setDeliveryType(type: DeliveryType | null): void {
    sessionStore.update(state => ({...state, selectedDeliveryType: type}));
  }

  replayOrder(order: PastOrderDto): Observable<PastOrderDto> {
    return of(order).pipe(
      switchMap(order => {
        if(this.basketRepository.totalCount === 0) {
          this.removeBasket();
          this.addReplayItems(order.items);
          this.setDeliveryAddress(order.deliveryPoint?.address || null, order.deliveryPoint?.coordinates || null);

          return of(order);
        }

        return this.showResetBasketDialog$().pipe(
          tap(isReset => {
            if(isReset) {
              this.removeBasket();
            }
            this.addReplayItems(order.items);
            this.setDeliveryAddress(order.deliveryPoint?.address || null, order.deliveryPoint?.coordinates || null);
          }),
          map(() => order)
        );
      }),
      tap(() => {
        void this.router.navigate(['/ordering']);
      })
    );
  }

  showResetBasketDialog$(): Observable<boolean> {
    this.statusMessage.setStatusMessage({
      title: $localize`:@@MessageModalTitleWarning:Warning`,
      message: $localize`:@@DeleteDishesInCart:There are already dishes in your cart. Delete them?`,
      type: 'warning',
      dialog: {
        buttons: [
          {type: 'accent', text: $localize`:@@Delete:Delete`, onClick: () => this.statusMessage.setDialogState('accept')},
          {type: 'warn', text: $localize`:@@AddDishesToCart:Add dishes to cart`, onClick: () => this.statusMessage.setDialogState('deny')},
        ]
      }
    });

    return this.statusMessage.dialogSubj$.pipe(
      map(dialogStatus => dialogStatus === 'accept')
    );
  }

  addReplayItems(replayItems: (PastOrderItemDto | StoredOrderItem)[]): void {
    //prepare items to add
    replayItems.forEach(replayItem => {
      const basketItem: BasketItem = {} as BasketItem;

      const itemInfo = this.menuRepository.getItemBySizeSku(replayItem.sku);
      if(!itemInfo) {
        void this.router.navigate(['/']);
        this.statusMessage.setStatusMessage({
          title: $localize`:@@MessageModalTitleError:Error`,
          message: $localize`:@@ResponseErrorCreateNewOrder:Sorry, some items from the menu are not available.
          Create a new order please.`,
          type: 'error',
        });
        return;
      }

      basketItem.item = itemInfo.item;
      basketItem.size = itemInfo.size;
      basketItem.count = replayItem.quantity;
      basketItem.itemKey = '';
      basketItem.appliedDiscount = 0;

      basketItem.modifiers = [];
      replayItem.modifiers.forEach(modifier => {
        const findMod = this.getModifierBySku(itemInfo.size, modifier.sku);
        if(!findMod) {
          return;
        }
        findMod.quantity = modifier.quantity;
        basketItem.modifiers.push(findMod);
      });

      basketItem.totalPrice = this.priceCalculationService.getItemTotal(
        itemInfo.size.prices,
        basketItem.modifiers,
        replayItem.quantity,
        this.configRepository.activeOutletId
      );

      this.basketRepository.addItem(basketItem);
    });
  }

  private getModifierBySku(size: MenuItemSizeDto, sku: string): Modifier | null {
    return size.itemModifierGroups
      .flatMap(group => group.items.map(modifier => ({...modifier, itemGroupId: group.itemGroupId})))
      .map(modifier => ({
        freeQuantity: modifier.restrictions.freeQuantity,
        itemGroupId: modifier.itemGroupId,
        name: modifier.name,
        prices: modifier.prices,
        quantity: 0,
        sku: modifier.sku,
      } as Modifier))
      .find(modifier => modifier.sku === sku) ?? null;
  }

  getUnavailableItems(): Observable<BasketItem[]> {
    return combineLatest([
      this.menuRepository.stopListItems$,
      this.basketRepository.items$,
      this.configRepository.activeOutletId$
    ]).pipe(
      map(([stopList, basketItems, activeOutletId]) => {
        const unavailableItems: BasketItem[] = [];
        const stopListMap: Map<string, number> = new Map<string, number>();
        basketItems.forEach(basketItem => {
          if(!basketItem.item.itemId || basketItem.item.balance === undefined) {
            return;
          }

          if(stopList.has(basketItem.item.itemId)) {
            const balance = stopList.get(basketItem.item.itemId) as number;
            if(balance >= basketItem.count) {
              return;
            }
            unavailableItems.push({...basketItem, balance: balance});
            stopListMap.set(basketItem.itemKey, balance);
            return;
          }

          const outletPrice = basketItem.size.prices.find(p => p.storeId == activeOutletId);
          if(!outletPrice || outletPrice.price == null) {
            unavailableItems.push({...basketItem, balance: 0});
            stopListMap.set(basketItem.itemKey, 0);
            return;
          }

          if(basketItem.modifiers) {
            const modifiersAreAvailable = basketItem.modifiers.every(mod => {
              if(!mod.prices) {
                return false;
              }
              return mod.prices.find(p => p.storeId == activeOutletId)?.price !== null;
            });

            if(!modifiersAreAvailable) {
              unavailableItems.push({...basketItem, balance: 0});
              stopListMap.set(basketItem.itemKey, 0);
              return;
            }
          }

        });

        sessionStore.update(state => ({...state, balanceToApply: stopListMap}));

        return unavailableItems;
      })
    );
  }

  applyBalance(): void {
    const balanceToApply = sessionStore.query((state) => state.balanceToApply);
    // TODO: batching
    balanceToApply.forEach((value, key) => {
      if(value == 0) {
        this.basketRepository.removeItem(key);
      }
      else {
        this.basketRepository.updateCount(key, value);
      }
    });
    sessionStore.update(state => ({...state, balanceToApply: new Map<string, number>()}));
  }

  checkCoupon(couponNumber: string): Observable<boolean> {
    sessionStore.update(updateRequestStatus('couponStatusFetching', 'pending'));

    return this.basketServiceApi.apiV1BasketCouponCheckPost$Json({
      body: {
        couponNumber: couponNumber,
        storeId: this.configRepository.activeOutletId
      }
    }).pipe(
      map(response => {
        if(response.error) {
          throw new Error('coupon is not found');
        }
        return response.result;
      }),
      tap(() => sessionStore.update(updateRequestStatus('couponStatusFetching', 'success'))),
      catchError(error => {
        sessionStore.update(updateRequestStatus('couponStatusFetching', 'error', error));
        throw new Error('coupon is not found');
      })
    );
  }

  private checkAvailableSubj$: Subject<unknown> = new Subject();
  private checkBasketAvailable$: Observable<boolean> = this.checkAvailableSubj$.pipe(
    switchMap(() => this.menuRepository.isMenuLoad$),
    filter(isMenuLoad => isMenuLoad),
    switchMap(() => this.isBasketItemsSchedule$),
  );

  isBasketItemsSchedule$: Observable<boolean> = this.basketRepository.items$.pipe(
    first(),
    combineLatestWith(this.basket$.pipe(take(1))),
    map(([basketItems, basket]) => this.checkBasketItemsSchedule(basketItems, basket?.deliveryDateTime ?? null)),
    tap(removeItems => {
      if(!removeItems.length) {
        return;
      }

      const itemNames: string[] = [];
      removeItems.forEach(item => {
        itemNames.push(item.name);
        this.basketRepository.removeItem(item.key);
      });

      this.unsetDeliveryDateTime();

      this.statusMessage.setStatusMessage({
        title: $localize`:@@MessageModalTitleWarning:Warning`,
        message: [
          $localize`:@@MessageItemScheduleNotAvailable:Unfortunately, we do not prepare this dish at the time you selected for delivery. Try choosing something else.`,
          ...itemNames
        ],
        type: 'warning'
      });
    }),

    map(removeItems => !!removeItems.length)
  );

  private checkBasketItemsSchedule(basketItems: BasketItem[], deliveryDateTime: string | null): RemovableItems[] {
    const removeItems: RemovableItems[] = [];

    basketItems.forEach(item => {
      if(!this.menuRepository.isCategoryScheduleAvailable(item.item.categoryId, deliveryDateTime)) {
        removeItems.push({
          key: item.itemKey,
          name: item.item.name
        });
      }
    });

    return removeItems;
  }

  checkBasketAvailable(): void {
    this.checkAvailableSubj$.next(null);
  }

  private removeBasketSub: Subscription;

  removeBasket(basketClearRequest: boolean = false): void {
    const basketId = sessionStore.query(state => state.preprocessedBasketId);

    if(basketId && basketClearRequest) {
      this.removeBasketSub?.unsubscribe();
      this.removeBasketSub = this.basketServiceApi.apiV1BasketClearBasketIdPost$Json({basketId}).subscribe();
    }

    sessionStore.update(
      state => ({
        ...state,
        isMustProcess: true,
        isBonusPayment: false,
        tablePosId: null,
        isTableFromLink: false,
        tableId: null,
        numberOfGuests: 1,
        preprocessedBasketId: null,
        onlinePaymentOrderId: null,
        basket: null,
      }),
      updateRequestStatus('basketPreprocessing', 'idle'),
    );

    this.basketRepository.deleteAll();
  }

  saveBasket(
    paymentType: PaymentType | null,
    customer: CustomerDto | null,
    coupon: string | null,
    isAsap: boolean | null = null,
    deliveryDateTime: Date | null = null
  ): void {
    const storedIsBonusPayment = sessionStore.query(state => state.isBonusPayment);
    const bonusPaymentSum = sessionStore.query(state => state.maxBonusPayment);
    const basketId = sessionStore.query(state => state.preprocessedBasketId);

    if(customer) {
      customer.email = customer.email?.toLowerCase();
    }

    const basket: ProcessBasketRequestDto = {
      id: basketId,
      storeId: this.configRepository.activeOutletId,
      customer: customer,
      selectPayVal: paymentType ?? this.configRepository.activePaymentsType,
      selectedDeliveryType: this.selectedDeliveryType!,
      bonusPaymentSum: storedIsBonusPayment ? bonusPaymentSum : 0,
      items: []
    };

    basket.isAsap = !!isAsap;

    if(deliveryDateTime) {
      basket.deliveryDateTime = new Date(deliveryDateTime.getTime() - getTimeZoneOffset(this.configRepository.restoTimeZone) * 60 * 1000).toISOString();
    }

    if(this.configRepository.activeZoneId && this.configRepository.coordinates) {
      basket.coordinates = this.configRepository.coordinates;
      basket.deliveryZoneId = this.configRepository.activeZoneId;
    }

    if(coupon) {
      basket.coupon = coupon;
    }

    sessionStore.update(state => ({...state, basket}));
  }


  updateAll(data: ProcessBasketResponse): void {
    this.basketRepository.updateAll(data);
    sessionStore.update(
      state => ({
        ...state,
        maxBonusPayment: data.availableBonusPayment,
        isMustProcess: false,
        preprocessedBasketId: data.basketId,
        dtmTime: data.deliveryTime || null,
        processFullCost: data.fullCost,
        processTotalPrice: data.total,
        processDeliveryPrice: data.deliveryCost === 0 && data.deliveryTime === null ? null : data.deliveryCost,
        freeItems: data.freeItems || null
      }),
    );
  }

  deliveryCoordinates$: Observable<CoordinatesDto | null> = sessionStore.pipe(
    select(state => state.deliveryAddress?.coordinates || null),
  );

  deliveryAddress$: Observable<AddressDto | null> = sessionStore.pipe(
    select(state => state.deliveryAddress?.address),
    map(address => this.replaceStreet(address))
  );

  deliveryAddressCoordinate$: Observable<CoordinatesDto | null> = sessionStore.pipe(
    select(state => state.deliveryAddressCoordinate),
  );

  setDeliveryAddressCoordinate(coordinates: CoordinatesDto | null): void {
    sessionStore.update(setProp('deliveryAddressCoordinate', coordinates));
  }

  get deliveryAddress(): AddressDto | null {
    return this.replaceStreet(sessionStore.query(state => state.deliveryAddress?.address || null));
  }

  private replaceStreet(address: AddressDto | null | undefined): AddressDto | null {
    return address
      ? {...address, street: address.street?.replace('улица', 'ул. ').trim()}
      : null;
  }

  setDeliveryAddress(address: AddressDto | null, coordinates: CoordinatesDto | null = null): void {
    sessionStore.update(setProp('deliveryAddress', {address, coordinates}));
  }

  setFreeItems(freeItems: FreeItem[] | null): void {
    sessionStore.update(setProp('freeItems', freeItems));
  }

  freeItems$: Observable<UIMenuItemDto[] | null> = sessionStore.pipe(
    select(state => state.freeItems),
    map((freeItems: FreeItem[] | null) => {
      if(!freeItems || !freeItems.length) {
        return null;
      }

      const items = this.prepareFreeItems(freeItems);

      return items.length ? items : null;
    }),
  );

  marketingCampaigns$: Observable<Map<string, MarketingCampaign> | null> = sessionStore.pipe(
    select(state => state.freeItems),
    map((freeItems: FreeItem[] | null) => {
      if(!freeItems || !freeItems.length) {
        return null;
      }

      const freeItemsMap = new Map<string, string[]>();
      const marketingCampaigns: Map<string, MarketingCampaign> = freeItems.reduce((map, item) => {
        if(!map.has(item.marketingCampaignId)) {
          map.set(item.marketingCampaignId, {
            marketingCampaignId: item.marketingCampaignId,
            marketingCampaignName: item.marketingCampaignName,
            items: []
          });
        }

        const productItem = this.prepareFreeItem(item);
        if(productItem) {
          map.get(item.marketingCampaignId).items.push(productItem);
        }

        freeItemsMap.set(item.categoryId + item.categoryId, item.sizeSkus);

        return map;
      }, new Map());

      sessionStore.update(setProp('freeItemsMap', freeItemsMap));
      return marketingCampaigns;
    }),
    shareReplay({refCount: false, bufferSize: 1}),
  );

  private prepareFreeItems(freeItems: FreeItem[]): UIMenuItemDto[] {
    const items: UIMenuItemDto[] = [];

    freeItems.forEach(freeItem => {
      const item = this.prepareFreeItem(freeItem);
      if(!item) {
        return;
      }
      items.push(item);
    });

    return items;
  }

  private prepareFreeItem(freeItem: FreeItem): UIMenuItemDto | null {
    const item = structuredClone(this.menuRepository.getItem(freeItem.categoryId + freeItem.itemId));
    if(!item) {
      return null;
    }

    const freeSizes = this.getFreeItemSizes(freeItem.sizeSkus, item.itemSizes);
    if(!freeSizes.length) {
      return null;
    }

    return {
      ...item,
      uniqKey: 'gift_' + item.uniqKey,
      balance: 1,
      itemSizes: freeSizes
    };
  }

  private getFreeItemSizes(sizeSkus: string[], itemSizes: MenuItemSizeDto[]): MenuItemSizeDto[] {
    if(!sizeSkus.length) {
      return itemSizes;
    }
    const newItemSizes = itemSizes.filter(size => sizeSkus.includes(size.sku));
    return newItemSizes;
  }

  toggleActiveLabelFilter(label: string): void {
    const currentFilter = sessionStore.query(state => state.activeLabelFilter);

    const activeLabelFilter = currentFilter?.includes(label)
      ? currentFilter.filter(i => i !== label)
      : [...currentFilter ?? [], label];

    sessionStore.update(setProp('activeLabelFilter', activeLabelFilter));
  }

  toggleActiveAllergenFilter(allergen: string): void {
    const currentFilter = sessionStore.query(state => state.activeAllergenFilter);

    const activeAllergenFilter = currentFilter?.includes(allergen)
      ? currentFilter.filter(i => i !== allergen)
      : [...currentFilter ?? [], allergen];

    sessionStore.update(setProp('activeAllergenFilter', activeAllergenFilter));
  }

  toggleShowFilterBar(status: boolean | null = null): void {
    sessionStore.update(state => ({
      ...state,
      showFilterBar: status === null ? !state.showFilterBar : status,
    }));
  }

  clearActiveFilter(): void {
    sessionStore.update(state => ({
      ...state,
      activeLabelFilter: [],
      activeAllergenFilter: [],
    }));
  }

  freeItemsMap$: Observable<Map<string, string[]> | null> = sessionStore.pipe(select(state => state.freeItemsMap));

  basketItems$: Observable<BasketItem[]> = this.basketRepository.items$.pipe(
    combineLatestWith(this.freeItemsMap$),
    map(([basketItems, freeItemsMap]) => {
      if(!freeItemsMap) {
        return basketItems;
      }

      return basketItems.map(item => {
        const freeItem = freeItemsMap.get(item.item.uniqKey);
        if(freeItem && freeItem.includes(item.size.sku)) {
          item.size.prices.forEach(price => {
            price.price = 0;
          });
        }
        return item;
      });
    })
  );

  isShowMap$: Observable<boolean> = this.configRepository.isMapInFeatures$.pipe(
    filter(isMapInFeatures => isMapInFeatures),
    switchMap(() => this.configRepository.addressFieldSettings$),
    combineLatestWith(this.isDelivery$),
    map(([addressFieldSettings, isDelivery]) => {
      return isDelivery ? addressFieldSettings.useAddressFormatType : true;
    })
  );
}
